import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, FormInstance, Select, Form, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { omit } from 'lodash';
import { useMediaQuery, usePageProvider } from '../../../hooks';
import { notificationFailed } from '../../../../infrastructure';
import { Store } from 'antd/lib/form/interface';

export interface DrawerDetailProps extends DrawerProps {
  value?: any;
  showDataTypeSelector?: boolean;
  forceRequest?: boolean;
  initialValues?: Store;
  ignoreKeyUpdate?: string[];
  ignoreKeyDuplicate?: string[];
  onValuesChange?(item: any, values: any, form: FormInstance): void;
  handleAfterSave?: (response?: any) => void;
  onShow: () => void;
}

const optionsDataType = [
  { label: 'Original', value: 'original' },
  { label: 'Requested', value: 'requested' },
];

export function DrawerDetail(props: DrawerDetailProps) {
  const {
    value,
    showDataTypeSelector = true,
    forceRequest = false,
    children,
  } = props;

  const [form] = Form.useForm();
  const { dataSource, transformer, moduleKey } = usePageProvider();

  const [loadingDetail, setLoadingDetail] = useState(false);

  const [dataDetail, setDataDetail] = useState(null);
  const [dataTypeValue, setDataTypeValue] = useState(
    optionsDataType?.find(item => item?.value === 'original')
  );

  /*Initial data for responsive drawer form*/
  const isTablet = useMediaQuery({ media: 'tablet' });
  const width = props.width ?? 500;
  const responsiveWidth = isTablet ? '100%' : width;

  /*Initial data to set default title drawer form*/
  const title = props.title ?? 'Data';
  const drawerTitle = `Detail ${title}`;

  const InitDrawerProps: DrawerProps = {
    ...omit(props ?? {}, ['value']),
    width: responsiveWidth,
    title: drawerTitle,
  };

  useEffect(() => {
    if (value && forceRequest) handleGetData(value?.id);
    else transformerPayload(value);

    //Reset data type to original when data has value
    setDataTypeValue(optionsDataType?.find(item => item?.value === 'original'));
  }, [value, forceRequest]);

  function transformerGetData(payload: any) {
    if (transformer?.transformerGetData)
      return transformer.transformerGetData(payload);
    return payload;
  }

  function makeDataDetail(payload: any): void {
    form.setFieldsValue({ id: payload?.id, detail_data: payload });
  }

  function transformerPayload(payload: any): void {
    let resultPayload = payload;
    if (transformerGetData) resultPayload = transformerGetData(payload);
    makeDataDetail(resultPayload);
    setDataDetail(resultPayload);
  }

  async function handleGetData(id: string): Promise<void> {
    setLoadingDetail(true);
    await dataSource.handleGetData(id, {
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
    setLoadingDetail(false);
  }

  function handleChangeDataType(row: any): void {
    let selectedDataType = optionsDataType?.find(
      item => item?.value === 'original'
    );

    //Initial original and requested data
    const originalData = value;
    const requestedData = dataDetail?.requested_data;

    if (row)
      selectedDataType = optionsDataType?.find(item => item?.value === row);
    if (row === 'requested') transformerPayload(requestedData);
    else transformerPayload(originalData);
    setDataTypeValue(selectedDataType);
  }

  function ExtraDrawer() {
    return (
      showDataTypeSelector &&
      !value?.has_requested_process &&
      !!value?.requested_data && (
        <Select
          value={dataTypeValue}
          onChange={handleChangeDataType}
          style={{ width: '120px' }}
          options={optionsDataType}
        />
      )
    );
  }

  return (
    <Drawer
      style={{ overflow: 'hidden' }}
      {...InitDrawerProps}
      extra={<ExtraDrawer />}
    >
      <Spin
        spinning={loadingDetail}
        indicator={<LoadingOutlined />}
        style={{ minHeight: '50vh' }}
      >
        <Form layout="vertical" form={form} name={`drawer-detail-${moduleKey}`}>
          <Form.Item noStyle hidden name="id"></Form.Item>
          <Form.Item noStyle hidden name="detail_data"></Form.Item>
          {children}
        </Form>
      </Spin>
    </Drawer>
  );
}
